import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FolderLock, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function getToken() {
  return localStorage.getItem("token") || "";
}

const STATUS_LABEL = {
  open: "Open",
  monitoring: "Monitoring",
  steward_review: "Steward Review",
  grievance_drafted: "Grievance Drafted",
  closed: "Closed",
};

const STATUS_COLOR = {
  open: "#22c55e",
  monitoring: "#f59e0b",
  steward_review: "#60a5fa",
  grievance_drafted: "#a78bfa",
  closed: "#71717a",
};

/**
 * Renders a compact chip linking to the Case Locker case attached to the
 * current Fact Finder or Grievance draft. Shows ONLY when there is exactly
 * one linked case, per the spec.
 *
 * Exposes a `refreshKey` prop: bump it after saving/linking to force a
 * re-fetch without unmounting the component.
 *
 * Props:
 *   sourceType: "fact_finder" | "grievance" | "statement"
 *   sourceId:   the draft id (omit/null if draft not saved yet)
 *   refreshKey: any value; changing it triggers a re-fetch
 */
export default function LinkedCaseChip({ sourceType, sourceId, refreshKey }) {
  const navigate = useNavigate();
  const [linked, setLinked] = useState(null); // single case or null

  const load = useCallback(async () => {
    if (!sourceId) { setLinked(null); return; }
    try {
      const res = await fetch(
        `${API}/api/case-locker/cases/linked?source_type=${encodeURIComponent(sourceType)}&source_id=${encodeURIComponent(sourceId)}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      if (!res.ok) { setLinked(null); return; }
      const data = await res.json();
      const cases = data.cases || [];
      // Spec: show ONLY when exactly one linked case
      setLinked(cases.length === 1 ? cases[0] : null);
    } catch {
      setLinked(null);
    }
  }, [sourceType, sourceId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!linked) return null;

  const label = STATUS_LABEL[linked.status] || linked.status || "";
  const dotColor = STATUS_COLOR[linked.status] || "#71717a";

  return (
    <button
      type="button"
      className="lcchip"
      data-testid="linked-case-chip"
      onClick={() => navigate(`/case-locker/${linked.case_id}`)}
      title={`Open linked case ${linked.case_id}`}
    >
      <style>{lcchipStyles}</style>
      <FolderLock size={12} />
      <span className="lcchip-label">
        Case: <b>{linked.case_id}</b>
        <span className="lcchip-dot" style={{ background: dotColor }} />
        <span className="lcchip-status">{label}</span>
      </span>
      <ExternalLink size={11} className="lcchip-ext" />
    </button>
  );
}

const lcchipStyles = `
.lcchip {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(139,92,246,0.10);
  border: 1px solid rgba(139,92,246,0.28);
  color: #c4b5fd;
  font-size: 12px; font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s, border-color 0.15s, transform 0.1s;
  line-height: 1;
}
.lcchip:hover { background: rgba(139,92,246,0.18); border-color: rgba(139,92,246,0.45); }
.lcchip:active { transform: translateY(1px); }
.lcchip svg { flex-shrink: 0; color: #a78bfa; }
.lcchip-label { display: inline-flex; align-items: center; gap: 6px; }
.lcchip-label b {
  color: #ddd6fe;
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 11px;
  font-weight: 700;
}
.lcchip-dot {
  width: 6px; height: 6px; border-radius: 50%;
  display: inline-block;
}
.lcchip-status { color: #a3a3a3; font-weight: 500; }
.lcchip-ext { opacity: 0.55; }
`;
